import { configureHuddleCamera, huddleCameraConstraints, type HuddleCameraPreferences } from './HuddleCamera';

export type HuddleSource = 'microphone' | 'camera' | 'screen';
export interface HuddleMediaIssue { source: HuddleSource; reason: string }
export type HuddleDeviceSource = Exclude<HuddleSource, 'screen'>;
export type HuddleDeviceSelection = Partial<Record<HuddleDeviceSource | 'speaker', string>>;
export interface HuddlePrejoinChoices { microphone: boolean; camera: boolean; deviceIds: HuddleDeviceSelection; cameraPreferences?: HuddleCameraPreferences }
export const HUDDLE_SOURCES: HuddleSource[] = ['microphone', 'camera', 'screen'];

export function huddleCaptureConstraints(source: HuddleDeviceSource, deviceId?: string, cameraPreferences?: HuddleCameraPreferences): MediaStreamConstraints {
  const device = deviceId ? { deviceId: { exact: deviceId } } : {};
  return source === 'microphone'
    ? { audio: { echoCancellation: true, noiseSuppression: true, ...device }, video: false }
    : { audio: false, video: { ...huddleCameraConstraints(cameraPreferences), ...device } };
}

/** Owns capture streams, including permission requests that resolve after leaving. */
export class HuddleMedia {
  private stopped = false;
  private versions: Record<HuddleSource, number> = { microphone: 0, camera: 0, screen: 0 };
  private streams = new Map<HuddleSource, MediaStream>();
  private pending = new Map<HuddleSource, MediaStream>();
  constructor(
    private readonly publish: (source: HuddleSource, track: MediaStreamTrack | null) => Promise<void>,
    private readonly changed: () => void,
    private readonly issue: (issue: HuddleMediaIssue) => void,
  ) {}
  track(source: HuddleSource): MediaStreamTrack | undefined { return this.streams.get(source)?.getTracks()[0]; }
  async enable(source: HuddleSource, deviceId?: string, cameraPreferences?: HuddleCameraPreferences): Promise<boolean> {
    if (this.stopped || this.track(source)) return !this.stopped;
    const version = ++this.versions[source];
    let stream: MediaStream | undefined;
    try {
      if (!navigator.mediaDevices) throw new DOMException('Media unavailable', 'NotFoundError');
      stream = source === 'screen'
        ? await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
        : await navigator.mediaDevices.getUserMedia(huddleCaptureConstraints(source, deviceId, source === 'camera' ? cameraPreferences : undefined));
      if (this.stopped || version !== this.versions[source]) { stream.getTracks().forEach((track) => track.stop()); return false; }
      const track = source === 'microphone' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
      if (!track) throw new DOMException('Media unavailable', 'NotFoundError');
      const ended = () => track.readyState === 'ended';
      if (ended()) throw new DOMException('Media disconnected', 'NotFoundError');
      // Do not retain unexpected tracks (for example screen audio) from a browser.
      stream.getTracks().filter((extra) => extra !== track).forEach((extra) => extra.stop());
      this.pending.set(source, stream);
      if (source === 'camera') {
        await configureHuddleCamera(track, cameraPreferences);
        if (this.stopped || version !== this.versions[source]) { stream.getTracks().forEach((item) => item.stop()); return false; }
        if (ended()) throw new DOMException('Media disconnected', 'NotFoundError');
      }
      await this.publish(source, track);
      if (this.stopped || version !== this.versions[source]) { stream.getTracks().forEach((item) => item.stop()); return false; }
      this.pending.delete(source);
      this.streams.set(source, new MediaStream([track]));
      track.addEventListener('ended', () => { if (this.track(source) === track) void this.disable(source); }, { once: true });
      this.changed();
      return true;
    } catch (error) {
      stream?.getTracks().forEach((track) => track.stop());
      if (this.pending.get(source) === stream) this.pending.delete(source);
      if (!this.stopped && version === this.versions[source]) {
        this.issue({ source, reason: error instanceof Error ? error.name : 'UnknownError' });
      }
      return false;
    }
  }
  async disable(source: HuddleSource): Promise<void> {
    this.versions[source] += 1;
    this.pending.get(source)?.getTracks().forEach((track) => track.stop());
    this.pending.delete(source);
    const stream = this.streams.get(source);
    this.streams.delete(source);
    stream?.getTracks().forEach((track) => track.stop());
    if (!this.stopped) {
      try { await this.publish(source, null); } catch { /* Leaving already disposes the sender. */ }
      this.changed();
    }
  }
  async listenOnly(): Promise<void> { await Promise.all(HUDDLE_SOURCES.map((source) => this.disable(source))); }
  stop(): void {
    this.stopped = true;
    HUDDLE_SOURCES.forEach((source) => { this.versions[source] += 1; });
    this.streams.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    this.pending.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    this.pending.clear();
    this.streams.clear();
  }
}
